/**
 * Auto-detection logic for unit-to-gallery-folder mapping.
 * Extracts potential unit numbers from folder names and matches to database units.
 */
import type { PropertyUnit } from '@/lib/units';

export type Confidence = 'high' | 'medium' | 'low' | 'none';

export interface FolderMappingSuggestion {
  folderName: string;
  suggestedUnitIds: string[];
  extractedIdentifiers: string[];
  confidence: Confidence;
  reason?: string;
}

/** Image extensions to consider (exclude PDFs) */
export const GALLERY_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.avif'];

/**
 * Normalize a folder name for Supabase storage: lowercase, spaces → hyphens.
 * Examples: "Unit 1 - 4 - 6 - 9" → "unit-1-4-6-9", "SS" → "ss"
 */
export function normalizeFolderNameForStorage(folderName: string): string {
  return folderName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-') // collapse multiple hyphens
    .replace(/^\-|\-$/g, ''); // trim leading/trailing hyphens
}

/**
 * Extract potential unit identifiers from a folder name.
 * Handles patterns like:
 * - "Unit 1 - 4 - 6 - 9" → ["1", "4", "6", "9"]
 * - "Unit 10" → ["10"]
 * - "Unit 11A" → ["11A"]
 * - "Unit 22A - 24A" → ["22A", "24A"]
 * - "Unit 25A 31A" → ["25A", "31A"]
 * - "SS" → []
 */
export function extractUnitIdentifiersFromFolderName(folderName: string): string[] {
  const trimmed = folderName.trim();
  if (!trimmed) return [];

  // Pattern: "Unit X" or "Unit X - Y - Z" or "Unit X Y Z" (space or " - " separated)
  const unitPrefixMatch = /^Unit\s+/i.exec(trimmed);
  if (unitPrefixMatch) {
    const rest = trimmed.slice(unitPrefixMatch[0].length).trim();
    // Split by " - " or " & " or multiple spaces, then trim
    const parts = rest.split(/\s*[-&]\s*|\s+/).map((p) => p.trim()).filter(Boolean);
    return parts;
  }

  // Standalone short identifier e.g. "SS" - could map to a unit if one exists with that number
  if (/^[A-Za-z0-9]+$/.test(trimmed) && trimmed.length <= 4) {
    return [trimmed];
  }

  return [];
}

/**
 * Build a lookup from unit_number (normalized for matching) to unit.
 * Database unit_number can be: "1", "10", "11A", "12A", "12B", "A11", "B11", etc.
 */
function buildUnitNumberToUnitMap(units: PropertyUnit[]): Map<string, PropertyUnit> {
  const map = new Map<string, PropertyUnit>();
  for (const u of units) {
    const key = (u.unit_number || '').trim();
    if (key) map.set(key, u);
    // Also store lowercase for case-insensitive match when folder says "11A"
    const keyLower = key.toLowerCase();
    if (keyLower !== key) map.set(keyLower, u);
  }
  return map;
}

/**
 * Resolve extracted identifiers to unit IDs.
 * Tries exact match first, then normalized (e.g. "11a" → unit 11A).
 */
function resolveIdentifiersToUnitIds(
  identifiers: string[],
  unitMap: Map<string, PropertyUnit>
): string[] {
  const ids = new Set<string>();
  for (const id of identifiers) {
    const trimmed = id.trim();
    const exact = unitMap.get(trimmed);
    if (exact) {
      ids.add(exact.id);
      continue;
    }
    const lower = trimmed.toLowerCase();
    const byLower = unitMap.get(lower);
    if (byLower) {
      ids.add(byLower.id);
      continue;
    }
    // Try without leading zero: "02" -> "2"
    if (/^\d+$/.test(trimmed)) {
      const noZero = parseInt(trimmed, 10).toString();
      const u = unitMap.get(noZero);
      if (u) ids.add(u.id);
    }
  }
  return Array.from(ids);
}

/**
 * Auto-detect suggested unit IDs for a folder name given the list of all units.
 */
export function suggestUnitsForFolder(
  folderName: string,
  units: PropertyUnit[]
): FolderMappingSuggestion {
  const identifiers = extractUnitIdentifiersFromFolderName(folderName);
  const unitMap = buildUnitNumberToUnitMap(units);

  if (identifiers.length === 0) {
    return {
      folderName,
      suggestedUnitIds: [],
      extractedIdentifiers: [],
      confidence: 'none',
      reason: 'No unit identifiers found in folder name',
    };
  }

  const suggestedUnitIds = resolveIdentifiersToUnitIds(identifiers, unitMap);

  let confidence: Confidence = 'medium';
  let reason: string | undefined;

  if (suggestedUnitIds.length === 0) {
    confidence = 'none';
    reason = `Extracted [${identifiers.join(', ')}] but no matching units in database`;
  } else if (suggestedUnitIds.length === identifiers.length) {
    confidence = 'high';
    reason = `Matched all ${identifiers.length} identifier(s)`;
  } else {
    confidence = 'low';
    reason = `Matched ${suggestedUnitIds.length} of ${identifiers.length} identifier(s)`;
  }

  return {
    folderName,
    suggestedUnitIds,
    extractedIdentifiers: identifiers,
    confidence,
    reason,
  };
}

/**
 * Run auto-detection for all folder names and return suggestions.
 */
export function suggestMappingsForAllFolders(
  folderNames: string[],
  units: PropertyUnit[]
): FolderMappingSuggestion[] {
  return folderNames.map((name) => suggestUnitsForFolder(name, units));
}
